'use client';

import * as React from 'react';
import { Autocomplete as BaseAutocomplete } from '@base-ui/react/autocomplete';
import { clsx } from 'clsx';
import styles from './Autocomplete.module.css';

/**
 * Marker object appended to the item list when `allowCustomValue` is set and
 * the typed query doesn't match an existing option.
 */
interface CreateOption {
  __create: true;
  inputValue: string;
}

function isCreateOption(item: unknown): item is CreateOption {
  return typeof item === 'object' && item !== null && '__create' in item;
}

export interface AutocompleteProps<T> {
  /** The full set of options. Filtering against the query is done internally. */
  items: readonly T[];

  /** Called with the option the user picked. */
  onSelect: (item: T) => void;

  /** Text used for filtering and for the input's value. Defaults to `String(item)`. */
  getOptionLabel?: (item: T) => string;

  /** React key for an option. Defaults to its label. */
  getOptionKey?: (item: T) => React.Key;

  /** Custom row rendering — e.g. a logo alongside the label. Defaults to the label. */
  renderOption?: (item: T) => React.ReactNode;

  /** Allows the user to submit a value that isn't in `items`. Requires `onCreate`. */
  allowCustomValue?: boolean;

  /** Called with the raw query when the user picks the "create" row. */
  onCreate?: (value: string) => void;

  /** Label for the create row. Defaults to `Add "<value>"`. */
  createOptionLabel?: (value: string) => React.ReactNode;

  /**
   * Controls the input's text. Pass alongside `onInputChange` when the typed
   * text is itself the value (a free-text field with suggestions) rather than
   * just a query over `items`. Leave unset for the uncontrolled default.
   */
  inputValue?: string;

  /** Called with the input's text on every change, including selections. */
  onInputChange?: (value: string) => void;

  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  loading?: boolean;
  loadingText?: string;
  emptyText?: string;

  /** Height/typography scale of the input. @default 'md' */
  size?: 'md' | 'lg';

  /** Clears the query after a selection is made. @default true */
  clearOnSelect?: boolean;

  className?: string;
  inputClassName?: string;
  name?: string;
  id?: string;
}

function ClearIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="m4.5 4.5 7 7m-7 0 7-7" />
    </svg>
  );
}

export function Autocomplete<T>({
  items,
  onSelect,
  getOptionLabel = (item) => String(item),
  getOptionKey,
  renderOption,
  allowCustomValue = false,
  onCreate,
  createOptionLabel,
  inputValue,
  onInputChange,
  label,
  placeholder,
  helperText,
  error,
  disabled = false,
  autoFocus = false,
  loading = false,
  loadingText = 'Loading…',
  emptyText = 'No results',
  size = 'md',
  clearOnSelect = true,
  className,
  inputClassName,
  name,
  id,
}: AutocompleteProps<T>) {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const [uncontrolledQuery, setUncontrolledQuery] = React.useState('');
  const query = inputValue ?? uncontrolledQuery;
  const setQuery = (value: string) => {
    if (inputValue === undefined) {
      setUncontrolledQuery(value);
    }
    onInputChange?.(value);
  };
  const { contains } = BaseAutocomplete.useFilter({ sensitivity: 'base' });

  const trimmedQuery = query.trim();

  const filtered = React.useMemo(() => {
    if (trimmedQuery === '') return items as readonly T[];
    return items.filter((item) => contains(getOptionLabel(item), trimmedQuery));
    // `contains` and `getOptionLabel` are stable enough in practice; recompute on
    // every query/items change.
  }, [items, trimmedQuery, contains, getOptionLabel]);

  const displayedItems = React.useMemo(() => {
    const list: Array<T | CreateOption> = [...filtered];
    if (
      allowCustomValue &&
      onCreate &&
      trimmedQuery !== '' &&
      !items.some((item) => getOptionLabel(item).toLowerCase() === trimmedQuery.toLowerCase())
    ) {
      list.push({ __create: true, inputValue: trimmedQuery });
    }
    return list;
  }, [filtered, allowCustomValue, onCreate, trimmedQuery, items, getOptionLabel]);

  const handlePick = (item: T | CreateOption) => {
    if (isCreateOption(item)) {
      onCreate?.(item.inputValue);
    } else {
      onSelect(item);
    }
    // The query is cleared in `onValueChange` — base-ui fills the input with the
    // picked item's label *after* this handler runs, so clearing here is undone.
  };

  const handleValueChange = (value: string, details?: { reason?: string }) => {
    if (clearOnSelect && details?.reason === 'item-press') {
      setQuery('');
      return;
    }
    setQuery(value);
  };

  const showLoading = loading && trimmedQuery === '';
  const showEmpty = !loading && displayedItems.length === 0 && trimmedQuery !== '';

  return (
    <div className={clsx(styles.container, className)}>
      {label && (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      )}
      <BaseAutocomplete.Root
        items={displayedItems}
        // Filtering is done above so the "create" row survives it.
        filter={null}
        autoHighlight
        value={query}
        onValueChange={handleValueChange}
        itemToStringValue={(item: T | CreateOption) =>
          isCreateOption(item) ? item.inputValue : getOptionLabel(item)
        }
        disabled={disabled}
        name={name}
      >
        <BaseAutocomplete.InputGroup
          className={clsx(
            styles.inputGroup,
            size === 'lg' && styles.sizeLg,
            disabled && styles.disabled,
            error && styles.error
          )}
        >
          <BaseAutocomplete.Input
            id={inputId}
            placeholder={placeholder}
            className={clsx(styles.input, inputClassName)}
            autoFocus={autoFocus}
          />
          {query !== '' && (
            <BaseAutocomplete.Clear className={styles.clear} aria-label="Clear">
              <ClearIcon />
            </BaseAutocomplete.Clear>
          )}
        </BaseAutocomplete.InputGroup>

        <BaseAutocomplete.Portal>
          <BaseAutocomplete.Positioner className={styles.positioner} sideOffset={4} align="start">
            <BaseAutocomplete.Popup className={styles.popup}>
              <div className={styles.viewport}>
                <BaseAutocomplete.Status>
                  {showLoading ? <div className={styles.status}>{loadingText}</div> : null}
                </BaseAutocomplete.Status>
                <BaseAutocomplete.Empty>
                  {showEmpty ? <div className={styles.empty}>{emptyText}</div> : null}
                </BaseAutocomplete.Empty>
                <BaseAutocomplete.List>
                  {(item: T | CreateOption, index: number) =>
                    isCreateOption(item) ? (
                      <BaseAutocomplete.Item
                        key="__create"
                        index={index}
                        value={item}
                        className={clsx(styles.item, styles.createItem)}
                        onClick={() => handlePick(item)}
                      >
                        {createOptionLabel ? (
                          createOptionLabel(item.inputValue)
                        ) : (
                          <span>
                            Add <strong>&ldquo;{item.inputValue}&rdquo;</strong>
                          </span>
                        )}
                      </BaseAutocomplete.Item>
                    ) : (
                      <BaseAutocomplete.Item
                        key={getOptionKey ? getOptionKey(item) : getOptionLabel(item)}
                        index={index}
                        value={item}
                        className={styles.item}
                        onClick={() => handlePick(item)}
                      >
                        {renderOption ? renderOption(item) : getOptionLabel(item)}
                      </BaseAutocomplete.Item>
                    )
                  }
                </BaseAutocomplete.List>
              </div>
            </BaseAutocomplete.Popup>
          </BaseAutocomplete.Positioner>
        </BaseAutocomplete.Portal>
      </BaseAutocomplete.Root>
      {helperText && !error && <span className={styles.helperText}>{helperText}</span>}
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
}

Autocomplete.displayName = 'Autocomplete';

export default Autocomplete;
